<!-- ================优质资源-课程详情====================-->
<template>
    <div class="premium-course-info-page">
        <home-head />
        <div class="premium-course-info-body">
            <div class="premium-course-info-inner" v-loading="loading">
                <!-- 标题区 -->
                <div class="course-info-header">
                    <div class="back-btn" @click="goBack">
                        <span class="back-arrow">←</span>
                        <span>返回</span>
                    </div>
                    <div class="course-info-title" :title="courseInfo.courseName">{{ courseInfo.courseName || '课程详情' }}</div>
                    <div class="header-placeholder"></div>
                </div>

                <!-- 主体内容 -->
                <div class="course-info-main" v-if="courseInfo.courseId">
                    <!-- 左侧工具栏 -->
                    <div class="course-info-toolbar">
                        <div class="toolbar-item" :class="{ active: showChapter }" @click="showChapter = !showChapter">
                            <el-icon class="toolbar-icon"><VideoPlay /></el-icon>
                            <span class="toolbar-text">章节</span>
                        </div>
                        <div class="toolbar-item" :class="{ active: fileList.length > 0 }" @click="handleMaterial">
                            <el-icon class="toolbar-icon"><Document /></el-icon>
                            <span class="toolbar-text">资料</span>
                        </div>
                        <div class="toolbar-item" :class="{ active: isCollect }" @click="handleCollect">
                            <el-icon class="toolbar-icon"><Star /></el-icon>
                            <span class="toolbar-text">收藏{{ courseInfo.collectCount || 0 }}</span>
                        </div>
                        <div class="toolbar-item" :class="{ active: isLike }" @click="handleLike">
                            <el-icon class="toolbar-icon"><Pointer /></el-icon>
                            <span class="toolbar-text">点赞{{ courseInfo.likeCount || 0 }}</span>
                        </div>
                    </div>

                    <!-- 章节目录树（浮动面板，不占主布局） -->
                    <div class="course-chapter-panel" :class="{ show: showChapter }">
                        <div class="chapter-panel-header">
                            <span>课程章节</span>
                            <el-icon class="chapter-close" @click="showChapter = false"><Close /></el-icon>
                        </div>
                        <div class="chapter-tree-wrapper">
                            <el-tree
                                v-if="dataSource.length > 0"
                                :data="dataSource"
                                highlight-current
                                ref="chapterTree"
                                node-key="courseId"
                                default-expand-all
                                :expand-on-click-node="false"
                                :props="{ label: 'courseName', id: 'courseId', children: 'childrenList' }"
                                @node-click="handleChapterClick">
                                <template #default="{ node, data }">
                                    <div class="custom-tree-node" :title="node.label">
                                        <el-icon :size="15" v-if="hasVideo(data)">
                                            <VideoPlay />
                                        </el-icon>
                                        <span class="tree-node-label">{{ node.label }}</span>
                                    </div>
                                </template>
                            </el-tree>
                            <div class="chapter-empty" v-else>暂无章节</div>
                        </div>
                    </div>

                    <!-- 中间内容区 -->
                    <div class="course-info-content" @click="showChapter = false">
                        <!-- 视频播放器 -->
                        <div class="video-player-box" id="videoWidth">
                            <video
                                id="videoElement"
                                v-if="isShowVideo && activeVideo.videoUrl"
                                class="video-player"
                                controls
                                controlsList="nodownload"
                                :poster="activeVideo.videoUrl + '?x-oss-process=video/snapshot,t_1,m_fast'"
                                preload="none"
                                :src="activeVideo.videoUrl">
                            </video>
                        </div>
                        <div class="video-empty" v-if="!activeVideo.videoUrl">
                            <img src="@/assets/image/data_empty.png" alt="暂无视频" />
                            <div>暂无视频</div>
                        </div>

                        <!-- 视频标签 / 节点 -->
                        <div class="video-node-list" v-if="videoNodeList.length > 0">
                            <el-tag
                                v-for="(item, index) in videoNodeList"
                                :key="index"
                                class="video-node-tag cursor"
                                type="info"
                                @click="seekToNode(item.nodeTime)">
                                <span class="node-name">{{ item.nodeName }}</span>
                                <span class="node-time">{{ item.nodeDateTime }}</span>
                            </el-tag>
                        </div>

                        <!-- 评论区域 -->
                        <div class="video-comments">
                            <div class="comments-header">
                                <div class="comments-count">
                                    <el-icon :size="18"><ChatDotSquare /></el-icon>
                                    <span>评论 {{ activeVideo.commentList ? activeVideo.commentList.length : 0 }}</span>
                                </div>
                                <div class="comment-input-box">
                                    <input
                                        class="comment-input"
                                        type="text"
                                        v-model="commentContent"
                                        placeholder="添加评论"
                                        @keyup.enter="submitComment" />
                                    <div class="send-btn" @click="submitComment">
                                        <el-icon :size="20"><Position /></el-icon>
                                    </div>
                                </div>
                            </div>
                            <div class="comments-list" v-if="activeVideo.commentList && activeVideo.commentList.length > 0">
                                <div class="comment-item" v-for="(item, index) in activeVideo.commentList" :key="index">
                                    <div class="comment-user">
                                        <el-avatar :size="32" :src="item.avatar" />
                                        <span class="comment-name">{{ item.trueName || '匿名用户' }}</span>
                                        <span class="comment-time" v-if="item.commentTime">{{ formatTime(item.commentTime) }}</span>
                                    </div>
                                    <div class="comment-content">{{ item.content }}</div>
                                </div>
                            </div>
                            <div class="comments-empty" v-else>
                                <div class="empty-icon">
                                    <img src="@/assets/image/data_empty.png" alt="暂无评论" />
                                </div>
                                <div class="empty-text">暂无评论</div>
                            </div>
                        </div>
                    </div>

                    <!-- 右侧信息栏 -->
                    <div class="course-info-sidebar" @click="showChapter = false">
                        <div class="sidebar-section">
                            <div class="teacher-info">
                                <div class="teacher-name">{{courseInfo.courseName }}</div>
                            </div>
                            <div class="course-recommend" v-if="courseInfo.courseRecommend">
                                {{ courseInfo.courseRecommend }}
                            </div>
                            <div class="course-meta">
                                <div class="meta-item" v-if="formatCourseRange">
                                    <span class="meta-label">学段/年级/学科</span>
                                    <span class="meta-value">{{ formatCourseRange }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="sidebar-section video-list-section" v-if="relatedVideos.length > 0">
                            <div class="section-title">视频列表</div>
                            <div class="related-video-item cursor"
                                 v-for="(item, index) in relatedVideos"
                                 :key="index"
                                 @click="switchVideo(item)">
                                <div class="related-video-cover">
                                    <img :src="item.videoUrl + '?x-oss-process=video/snapshot,t_1,m_fast'" alt="" />
                                    <div class="play-mask">
                                        <el-icon :size="24"><VideoPlay /></el-icon>
                                    </div>
                                </div>
                                <div class="related-video-title" :class="{ active: activeVideo.videoId == item.videoId }" :title="item.videoTitle">
                                    {{ item.videoTitle  }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 暂无数据 -->
                <div class="course-info-empty" v-else-if="!loading">
                    <div class="empty-icon">
                        <img src="@/assets/image/data_empty.png" alt="暂无数据" />
                    </div>
                    <div class="empty-text">暂无课程信息</div>
                </div>
            </div>
        </div>

        <!-- 资料弹框 -->
        <el-dialog v-model="materialDialogVisible" title="课程资料" width="700px" :close-on-click-modal="false">
            <div class="file-list" v-if="fileList && fileList.length > 0">
                <div class="file-item" v-for="(item, index) in fileList" :key="index" @click="handleFileClick(item)">
                    <el-icon class="file-icon"><Document /></el-icon>
                    <span class="file-name" :title="item.fileName">{{ item.fileName || '未命名文件' }}</span>
                </div>
            </div>
            <div class="file-empty" v-else>暂无资料</div>
        </el-dialog>
    </div>
</template>

<script>
import flvjs from "flv.js/dist/flv.min.js";
import { infoFamousCourse, viewFamousCourse, collectFamousCourses, likeFamousCourses } from "@/api/admin/course";
import { addOrCancelCollect, judgeIsCollect } from "@/api/admin/collect";
import { addOrCancelLike, judgeIsLike } from "@/api/admin/likes";
import { timestampToTime } from "@/utils/date";
import homeHead from "@/components/common/homeHead.vue";

export default {
    name: "premiumCourseInfo",
    components: { homeHead },
    data() {
        return {
            loading: false,
            courseId: "",
            courseInfo: {},
            isCollect: false,
            isLike: false,
            collecting: false,
            liking: false,
            dataSource: [],
            activeObj: {},
            activeVideo: {
                videoUrl: "",
                commentList: []
            },
            videoNodeList: [],
            commentContent: "",
            showChapter: false,
            materialDialogVisible: false,
            fileList: [],
            isShowVideo: false,
            flvPlayer: null
        };
    },
    computed: {
        formatCourseRange() {
            const parts = [];
            if (this.courseInfo.periodName) parts.push(this.courseInfo.periodName);
            if (this.courseInfo.gradeName) parts.push(this.courseInfo.gradeName);
            if (this.courseInfo.subjectName) parts.push(this.courseInfo.subjectName);
            return parts.join("-") || "";
        },
        teacherAvatar() {
            return (this.courseInfo.userAvatar || "") + "?_=" + Date.now();
        },
        relatedVideos() {
            const list = [];
            const collect = (data) => {
                if (!data) return;
                if (data.videoList && data.videoList.length > 0) {
                    data.videoList.forEach(v => list.push(v));
                }
                if (data.childrenList && data.childrenList.length > 0) {
                    data.childrenList.forEach(child => collect(child));
                }
            };
            collect(this.courseInfo);
            return list;
        }
    },
    watch: {
        "$route.query.courseId": {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.courseId = newVal;
                    this.resetData();
                    this.queryCourseInfo();
                    this.judgeCollectAndLike();
                    this.addCourseView();
                }
            }
        }
    },
    created() {},
    mounted() {},
    beforeUnmount() {
        this.destroyPlayer();
    },
    methods: {
        /* ===============重置数据===================== */
        resetData() {
            this.destroyPlayer();
            this.isShowVideo = false;
            this.courseInfo = {};
            this.dataSource = [];
            this.activeObj = {};
            this.activeVideo = { videoUrl: "", commentList: [] };
            this.videoNodeList = [];
            this.commentContent = "";
            this.fileList = [];
            this.showChapter = false;
            this.isCollect = false;
            this.isLike = false;
            this.collecting = false;
            this.liking = false;
        },

        /* ===============查询课程详情===================== */
        queryCourseInfo() {
            this.loading = true;
            infoFamousCourse({ courseId: this.courseId }).then(res => {
                if (res.data.code === 1) {
                    this.courseInfo = res.data.data || {};
                    this.dataSource = [this.courseInfo];
                    this.fileList = this.courseInfo.fileList || [];
                    this.initDefaultVideo();
                } else {
                    this.$message.error(res.data.msg || "请求出错");
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },

        /* ===============增加课程观看数===================== */
        addCourseView() {
            viewFamousCourse({ courseId: this.courseId }).catch(() => {});
        },

        /* ===============初始化默认播放视频===================== */
        initDefaultVideo() {
            const info = this.courseInfo;
            let target = null;
            let targetObj = null;
            const findFirstVideo = (data) => {
                if (!data) return;
                if (data.videoList && data.videoList.length > 0) {
                    target = data.videoList[0];
                    targetObj = data;
                    return true;
                }
                if (data.childrenList && data.childrenList.length > 0) {
                    for (let child of data.childrenList) {
                        if (findFirstVideo(child)) return true;
                    }
                }
                return false;
            };
            if (info.videoList && info.videoList.length > 0) {
                target = info.videoList[0];
                targetObj = info;
            } else {
                findFirstVideo(info);
            }
            this.activeObj = targetObj || info;
            if (target) {
                this.activeVideo = { ...target };
                this.getVideoNodeList();
                this.initPlayer();
                this.$nextTick(() => {
                    if (this.$refs.chapterTree && targetObj) {
                        this.$refs.chapterTree.setCurrentKey(targetObj.courseId);
                    }
                });
            }
        },

        /* ===============章节点击===================== */
        handleChapterClick(data) {
            this.activeObj = data;
            if (data.videoList && data.videoList.length > 0) {
                this.activeVideo = { ...data.videoList[0] };
                this.getVideoNodeList();
                this.initPlayer();
            }
        },

        /* ===============判断是否有视频===================== */
        hasVideo(data) {
            return data.videoList && data.videoList.length > 0;
        },

        /* ===============切换视频===================== */
        switchVideo(item) {
            this.activeVideo = { ...item };
            this.getVideoNodeList();
            this.initPlayer();
        },

        /* ===============初始化视频播放器（支持 flv）===================== */
        initPlayer() {
            this.destroyPlayer();
            if (!this.activeVideo.videoUrl) return;
            this.isShowVideo = false;
            this.$nextTick(() => {
                this.isShowVideo = true;
                this.$nextTick(() => {
                    const videoElement = document.getElementById("videoElement");
                    if (!videoElement || !flvjs.isSupported()) return;
                    const videoUrl = this.activeVideo.videoUrl;
                    const videoType = videoUrl.substring(videoUrl.length - 3).toLowerCase();
                    this.flvPlayer = flvjs.createPlayer(
                        {
                            type: videoType,
                            isLive: true,
                            hasAudio: true,
                            url: videoUrl
                        },
                        {
                            enableWorker: false,
                            enableStashBuffer: false,
                            reuseRedirectedURL: true,
                            autoCleanupSourceBuffer: true
                        }
                    );
                    this.flvPlayer.attachMediaElement(videoElement);
                    this.flvPlayer.load();
                });
            });
        },

        /* ===============销毁视频播放器===================== */
        destroyPlayer() {
            if (this.flvPlayer) {
                try {
                    this.flvPlayer.pause();
                    this.flvPlayer.unload();
                    this.flvPlayer.detachMediaElement();
                    this.flvPlayer.destroy();
                } catch (e) {
                    console.log(e);
                }
                this.flvPlayer = null;
            }
        },

        /* ===============获取视频节点列表===================== */
        getVideoNodeList() {
            this.videoNodeList = [];
            if (this.activeVideo.nodeList && this.activeVideo.nodeList.length > 0) {
                this.videoNodeList = this.activeVideo.nodeList.map(e => {
                    const timeArr = this.formatSeconds(e.nodeTime).split(":");
                    return {
                        ...e,
                        nodeDateTime: timeArr[0] + ":" + timeArr[1] + ":" + timeArr[2]
                    };
                });
            }
        },

        /* ===============跳转到节点===================== */
        seekToNode(time) {
            const videoElement = document.getElementById("videoElement");
            if (videoElement && time !== undefined && time !== null) {
                videoElement.currentTime = Number(time);
                videoElement.play();
            }
        },

        /* ===============资料===================== */
        handleMaterial() {
            this.materialDialogVisible = true;
        },
        handleFileClick(item) {
            const url = item.fileUrl || item.url || "";
            if (url) {
                window.open(url, "_blank");
            } else {
                this.$message.info("该文件暂无可下载地址");
            }
        },

        /* ===============判断收藏/点赞状态===================== */
        judgeCollectAndLike() {
            judgeIsCollect({
                relationId: this.courseId,
                collectType: Number(this.$route.query.collectType) || 1,
                businessType: 6
            }).then(res => {
                if (res.data.code === 1) {
                    this.isCollect = !!res.data.data;
                }
            });
            judgeIsLike({ relationId: this.courseId }).then(res => {
                if (res.data.code === 1) {
                    this.isLike = !!res.data.data;
                }
            });
        },

        /* ===============收藏===================== */
        handleCollect() {
            if (this.collecting) return;
            this.collecting = true;
            addOrCancelCollect({
                relationId: this.courseId,
                collectType: Number(this.$route.query.collectType) || 1,
                businessType: 6
            }).then(res => {
                if (res.data.code === 1) {
                    this.isCollect = !this.isCollect;
                    this.$message.success(this.isCollect ? "收藏成功" : "已取消收藏");
                    this.updateCollectCount();
                } else {
                    this.$message.error(res.data.msg || "操作失败");
                }
            }).catch(() => {}).finally(() => {
                this.collecting = false;
            });
        },
        /*编辑课程收藏数：1-增加 2-扣减*/
        updateCollectCount() {
            collectFamousCourses({
                courseId: this.courseId,
                type: this.isCollect ? 1 : 2
            }).then(res => {
                if (res.data.code === 1) {
                    const count = Number(this.courseInfo.collectCount) || 0;
                    this.courseInfo.collectCount = Math.max(0, count + (this.isCollect ? 1 : -1));
                }
            }).catch(() => {});
        },

        /* ===============点赞===================== */
        handleLike() {
            if (this.liking) return;
            this.liking = true;
            addOrCancelLike({ relationId: this.courseId }).then(res => {
                if (res.data.code === 1) {
                    this.isLike = !this.isLike;
                    this.$message.success(this.isLike ? "点赞成功" : "已取消点赞");
                    this.updateLikeCount();
                } else {
                    this.$message.error(res.data.msg || "操作失败");
                }
            }).catch(() => {}).finally(() => {
                this.liking = false;
            });
        },
        /*编辑课程点赞数：1-增加 2-扣减*/
        updateLikeCount() {
            likeFamousCourses({
                courseId: this.courseId,
                type: this.isLike ? 1 : 2
            }).then(res => {
                if (res.data.code === 1) {
                    const count = Number(this.courseInfo.likeCount) || 0;
                    this.courseInfo.likeCount = Math.max(0, count + (this.isLike ? 1 : -1));
                }
            }).catch(() => {});
        },

        /* ===============提交评论===================== */
        submitComment() {
            if (!this.commentContent.trim()) {
                this.$message.error("请输入评论内容");
                return;
            }
            this.$message.info("评论功能开发中");
            this.commentContent = "";
        },

        /* ===============时间格式化===================== */
        formatTime(val) {
            if (!val) return "";
            return timestampToTime(val);
        },
        formatSeconds(ss) {
            var hh = 0;
            var mm = 0;
            var seconds = ~~ss;
            if (seconds > 3599) {
                hh = Math.floor(seconds / 3600);
                mm = Math.floor((seconds % 3600) / 60);
                seconds = seconds % 60;
                return (hh > 9 ? hh : "0" + hh) + ":" + (mm > 9 ? mm : "0" + mm) + ":" + (seconds > 9 ? seconds : "0" + seconds);
            } else if (seconds > 59) {
                mm = Math.floor(seconds / 60);
                seconds = seconds % 60;
                return "00:" + (mm > 9 ? mm : "0" + mm) + ":" + (seconds > 9 ? seconds : "0" + seconds);
            } else {
                return "00:00:" + (seconds > 9 ? seconds : "0" + seconds);
            }
        },

        /* ===============返回===================== */
        goBack() {
            this.$router.back();
        }
    }
};
</script>

<style scoped>
.premium-course-info-page {
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.premium-course-info-body {
    flex: 1;
    min-height: 0;
    width: 100%;
    background-color: #fdfcf8;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 30px 0;
    box-sizing: border-box;
    overflow: hidden;
}

.premium-course-info-inner {
    width: 100%;
    max-width: 1200px;
    height: 100%;
    margin: 0 auto;
    padding: 0 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
}

/* 标题区 */
.course-info-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-shrink: 0;
}

.course-info-title {
    flex: 1;
    text-align: center;
    font-size: 22px;
    color: #b22222;
    font-weight: bold;
    font-family: sans-serif;
    letter-spacing: 1px;
    padding: 0 20px;
    box-sizing: border-box;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.back-btn {
    height: 34px;
    line-height: 34px;
    padding: 0 28px;
    background-color: #b22222;
    color: #ffffff;
    border-radius: 4px;
    font-size: 14px;
    font-family: sans-serif;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.back-btn:hover {
    background-color: #9a1b1b;
}

.back-arrow {
    margin-right: 8px;
    font-size: 16px;
}

.header-placeholder {
    width: 108px;
    flex-shrink: 0;
}

/* 主体内容 */
.course-info-main {
    flex: 1;
    min-height: 0;
    width: 100%;
    background-color: #ffffff;
    border: 1.5px solid #b22222;
    border-radius: 4px;
    box-sizing: border-box;
    display: flex;
    overflow: hidden;
    padding: 16px;
    position: relative;
}

/* 左侧工具栏 */
.course-info-toolbar {
    width: 70px;
    flex-shrink: 0;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 0;
    box-sizing: border-box;
    background-color: #ffffff;
    position: relative;
    z-index: 100;
}

.toolbar-item {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 14px 0;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #4b5563;
}

.toolbar-item:hover {
    color: #b22222;
    background-color: #fef2f2;
}

.toolbar-item.active {
    color: #b22222;
}

.toolbar-icon {
    font-size: 22px;
    margin-bottom: 6px;
}

.toolbar-text {
    font-size: 12px;
    font-family: sans-serif;
    text-align: center;
    line-height: 1.4;
}

/* 章节面板（浮动，不占主布局） */
.course-chapter-panel {
    width: 300px;
    position: absolute;
    left: 70px;
    top: 16px;
    bottom: 16px;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 999;
    opacity: 0;
    width: 0;
    pointer-events: none;
    transition: all 0.3s ease;
}

.course-chapter-panel.show {
    width: 300px;
    opacity: 1;
}

.course-chapter-panel .chapter-panel-header,
.course-chapter-panel .chapter-tree-wrapper {
    pointer-events: auto;
}

.course-chapter-panel:not(.show) .chapter-panel-header,
.course-chapter-panel:not(.show) .chapter-tree-wrapper {
    pointer-events: none;
}

.chapter-panel-header {
    height: 42px;
    line-height: 42px;
    padding: 0 14px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 15px;
    color: #1f2937;
    font-weight: bold;
    font-family: sans-serif;
    flex-shrink: 0;
}

.chapter-close {
    cursor: pointer;
    color: #9ca3af;
    font-size: 16px;
}

.chapter-close:hover {
    color: #b22222;
}

.chapter-tree-wrapper {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 10px;
}

.chapter-empty {
    padding: 40px 0;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
}

.custom-tree-node {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
}

.tree-node-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 中间内容区 */
.course-info-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 6px;
    padding-left: 16px;
}

.video-player-box {
    width: 100%;
    background-color: #000000;
    border-radius: 4px;
    overflow: hidden;
    flex-shrink: 0;
}

.video-player {
    width: 100%;
    height: auto;
    max-height: 520px;
    display: block;
    outline: none;
}

.video-empty {
    width: 100%;
    height: 360px;
    background-color: #f3f4f6;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #9ca3af;
    font-size: 14px;
    flex-shrink: 0;
}

.video-empty img {
    width: 100px;
    height: 100px;
    object-fit: contain;
    margin-bottom: 10px;
}

.video-node-list {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    flex-shrink: 0;
}

.video-node-tag {
    display: flex;
    align-items: center;
    gap: 6px;
}

.node-name {
    font-weight: bold;
    color: #1f2937;
}

.node-time {
    color: #6b7280;
}

/* 评论区域 */
.video-comments {
    flex: 1;
    min-height: 0;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
}

.comments-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
    flex-shrink: 0;
}

.comments-count {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    color: #4b5563;
    font-family: sans-serif;
    flex-shrink: 0;
    white-space: nowrap;
}

.comments-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-top: 10px;
    padding-bottom: 10px;
    border-top: 1px solid #e5e7eb;
}

.comment-item {
    padding: 12px 0;
    border-bottom: 1px solid #f3f4f6;
}

.comment-user {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.comment-name {
    font-size: 14px;
    color: #4b5563;
    font-weight: 500;
}

.comment-time {
    font-size: 12px;
    color: #9ca3af;
    margin-left: auto;
}

.comment-content {
    padding-left: 42px;
    font-size: 14px;
    color: #1f2937;
    line-height: 1.6;
    word-break: break-all;
}

.comments-empty {
    flex: 1;
    min-height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-top: 1px solid #e5e7eb;
    padding-top: 10px;
}

.comments-empty .empty-icon {
    width: 40px;
    height: 40px;
}

.comments-empty .empty-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.empty-text {
    font-size: 14px;
    color: #9ca3af;
    font-family: sans-serif;
}

.comment-input-box {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    box-sizing: border-box;
    border-radius: 4px;
    flex-shrink: 0;
}

.comment-input {
    flex: 1;
    height: 32px;
    line-height: 32px;
    padding: 0 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    outline: none;
    font-size: 14px;
    font-family: sans-serif;
    box-sizing: border-box;
    background-color: #ffffff;
}

.comment-input:focus {
    border-color: #b22222;
}

.send-btn {
    width: 64px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #b22222;
    color: #ffffff;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.send-btn:hover {
    background-color: #9a1b1b;
}

/* 右侧信息栏 */
.course-info-sidebar {
    width: 280px;
    flex-shrink: 0;
    border-left: 1px solid #e5e7eb;
    margin-left: 16px;
    padding-left: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
}

.sidebar-section {
    margin-bottom: 20px;
    flex-shrink: 0;
}

.teacher-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
}

.teacher-name {
    font-size: 16px;
    color: #1f2937;
    font-weight: bold;
    font-family: sans-serif;
}

.course-recommend {
    font-size: 14px;
    color: #4b5563;
    line-height: 1.7;
    margin-bottom: 14px;
    word-break: break-all;
}

.course-meta {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.meta-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.meta-label {
    font-size: 12px;
    color: #9ca3af;
}

.meta-value {
    font-size: 14px;
    color: #1f2937;
}

.video-list-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.section-title {
    font-size: 15px;
    color: #1f2937;
    font-weight: bold;
    font-family: sans-serif;
    margin-bottom: 12px;
    flex-shrink: 0;
}

.related-video-item {
    margin-bottom: 14px;
}

.related-video-cover {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 4px;
    overflow: hidden;
    background-color: #f3f4f6;
    position: relative;
    margin-bottom: 6px;
}

.related-video-cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.play-mask {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.25);
    display: flex;
    justify-content: center;
    align-items: center;
    color: #ffffff;
    opacity: 0;
    transition: all 0.2s ease;
}

.related-video-item:hover .play-mask {
    opacity: 1;
}

.related-video-title {
    font-size: 13px;
    color: #4b5563;
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.related-video-title.active {
    color: #b22222;
    font-weight: bold;
}

/* 暂无数据 */
.course-info-empty {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.course-info-empty .empty-icon {
    width: 120px;
    height: 120px;
    margin-bottom: 16px;
}

.course-info-empty .empty-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

/* 资料弹框 */
.file-list {
    max-height: 400px;
    overflow-y: auto;
}

.file-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid #f3f4f6;
    cursor: pointer;
    transition: all 0.2s ease;
}

.file-item:hover {
    background-color: #fef2f2;
}

.file-icon {
    font-size: 20px;
    color: #b22222;
    margin-right: 10px;
    flex-shrink: 0;
}

.file-name {
    flex: 1;
    font-size: 14px;
    color: #1f2937;
    font-family: sans-serif;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.file-empty {
    padding: 40px 0;
    text-align: center;
    font-size: 14px;
    color: #9ca3af;
    font-family: sans-serif;
}

/* 滚动条统一 */
.course-info-content::-webkit-scrollbar,
.course-info-sidebar::-webkit-scrollbar,
.chapter-tree-wrapper::-webkit-scrollbar,
.comments-list::-webkit-scrollbar,
.file-list::-webkit-scrollbar {
    width: 10px;
    height: 10px; 
}

.course-info-content::-webkit-scrollbar-track,
.course-info-sidebar::-webkit-scrollbar-track,
.chapter-tree-wrapper::-webkit-scrollbar-track,
.comments-list::-webkit-scrollbar-track,
.file-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 6px;
}

.course-info-content::-webkit-scrollbar-thumb,
.course-info-sidebar::-webkit-scrollbar-thumb,
.chapter-tree-wrapper::-webkit-scrollbar-thumb,
.comments-list::-webkit-scrollbar-thumb,
.file-list::-webkit-scrollbar-thumb {
    background: #b22222;
    border-radius: 6px;
    border: 3px solid #f1f1f1;
}

.cursor {
    cursor: pointer;
}

@media (max-width: 1280px) {
    .premium-course-info-inner { padding: 0 16px; }
    .course-info-title { font-size: 20px; }
    .course-info-main { padding: 12px; }
    .course-info-sidebar { width: 240px; margin-left: 12px; padding-left: 12px; }
    .course-chapter-panel,
    .course-chapter-panel.show { width: 260px; }
}

@media (max-width: 1024px) {
    .course-info-main { flex-direction: column; overflow-y: auto; }
    .course-info-toolbar {
        width: 100%;
        flex-direction: row;
        border-right: none;
        border-bottom: 1px solid #e5e7eb;
        padding: 8px 0;
        position: static;
    }
    .toolbar-item { flex-direction: row; gap: 6px; padding: 8px 12px; }
    .toolbar-icon { margin-bottom: 0; font-size: 18px; }
    .course-chapter-panel,
    .course-chapter-panel.show {
        left: 12px;
        right: 12px;
        top: 56px;
        width: auto;
        height: 280px;
        bottom: auto;
    }
    .course-info-sidebar {
        width: 100%;
        border-left: none;
        margin-left: 0;
        padding-left: 0;
        border-top: 1px solid #e5e7eb;
        padding-top: 12px;
        margin-top: 12px;
    }
    .video-list-section {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
    }
    .section-title { grid-column: 1 / -1; }
}

@media (max-width: 768px) {
    .course-info-header { flex-direction: column; align-items: flex-start; gap: 12px; }
    .course-info-title { width: 100%; text-align: left; padding: 0; }
    .header-placeholder { display: none; }
    .video-list-section { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
